import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode
} from 'react'

import { LRUMap } from '@/utils/lru'

export interface ImpressionProviderProps {
  children: ReactNode
  syncInterval?: number
  dedupeInterval?: number
  maxLastImpressions?: number
  onSync: (impressions: Impression[]) => void
}

interface ImpressionMetadata {
  sponsored: boolean
  section: string
  page: string
}

export interface Impression {
  listingId: string
  metadata: ImpressionMetadata
}

export interface ImpressionContextType {
  addImpression: (listingId: string, metadata: ImpressionMetadata) => void
  registerObserver: (id: string, observer: IntersectionObserver) => void
  unregisterObserver: (id: string) => void
  getObserver: (id: string) => IntersectionObserver | null
}

export const ImpressionContext = createContext<
  ImpressionContextType | undefined
>(undefined)

export const ImpressionProvider: React.FC<ImpressionProviderProps> = ({
  children,
  syncInterval = 60000,
  dedupeInterval = 300000,
  maxLastImpressions = 1000,
  onSync
}) => {
  const impressions = useRef<Impression[]>([])
  const lastImpressionsRef = useRef(
    new LRUMap<string, number>(maxLastImpressions)
  )
  const observersRef = useRef(new LRUMap<string, IntersectionObserver>(10))
  const dirty = useRef(false)

  const addImpression = useCallback(
    (listingId: string, metadata: ImpressionMetadata) => {
      const now = Date.now()
      const key = `${listingId}:${metadata.page}:${metadata.section}:${metadata.sponsored}`
      const lastImpression = lastImpressionsRef.current.get(key) || 0
      if (now - lastImpression > dedupeInterval) {
        impressions.current = [
          ...impressions.current,
          {
            listingId,
            metadata
          }
        ]
        lastImpressionsRef.current.set(key, now)
        dirty.current = true
      }
    },
    [dedupeInterval]
  )

  const registerObserver = useCallback(
    (id: string, observer: IntersectionObserver) => {
      observersRef.current.set(id, observer)
    },
    []
  )

  const unregisterObserver = useCallback((id: string) => {
    observersRef.current.delete(id)
  }, [])

  const getObserver = useCallback((id: string) => {
    return observersRef.current.get(id) || null
  }, [])

  const syncImpressions = useCallback(async () => {
    if (dirty.current) {
      onSync(impressions.current)
      impressions.current = []
      dirty.current = false
    }
  }, [onSync])

  useEffect(() => {
    const intervalId = setInterval(syncImpressions, syncInterval)
    return () => clearInterval(intervalId)
  }, [syncImpressions, syncInterval])

  return (
    <ImpressionContext.Provider
      value={{
        addImpression,
        registerObserver,
        unregisterObserver,
        getObserver
      }}
    >
      {children}
    </ImpressionContext.Provider>
  )
}

export function useImpressionTracker() {
  const context = useContext(ImpressionContext)

  if (!context) {
    throw new Error(
      'useImpressionTracker must be used within an ImpressionProvider'
    )
  }

  return context
}
